/**
 * useViewSharedBasket — recipient-side read hook.
 * Responsible only for fetching and enriching the shared basket snapshot.
 * Write concern (add-to-cart) lives in useAddSharedBasketToCart.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { SHARE_ERROR_MESSAGES, SHARE_WARNING_MESSAGES } from '../constants/messages';
import { loadBasketByToken, loadCustomerGroupMap, loadProductById } from '../services/loadSharedBasket.service';
import { CtProduct, EnrichedBasket } from '../types/sharedBasket.types';

interface UseViewSharedBasketOptions {
  shareToken: string | undefined;
  showError: (message: string) => void;
}

type ErrorCode = 'expired' | 'not-found' | 'no-products' | 'general' | null;

export function useViewSharedBasket({
  shareToken,
  showError,
}: UseViewSharedBasketOptions) {
  const navigate = useNavigate();
  const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ErrorCode>(null);
  const [sharedBasket, setSharedBasket] = useState<EnrichedBasket | null>(null);

  /** Schedule a delayed redirect, cancelling any previously scheduled one. */
  const scheduleRedirect = useCallback(
    (path: string, delayMs = 3000) => {
      if (navTimerRef.current !== null) clearTimeout(navTimerRef.current);
      navTimerRef.current = setTimeout(() => navigate(path), delayMs);
    },
    [navigate],
  );

  const fetchSharedBasket = useCallback(async () => {
    if (!shareToken) return;

    setLoading(true);
    setError(null);

    try {
      const snapshot = await loadBasketByToken(shareToken).catch((err: Error & { code?: string }) => {
        if (err.code === 'EXPIRED') {
          setError('expired');
          showError(SHARE_ERROR_MESSAGES.EXPIRED_LINK);
          scheduleRedirect('/');
          return null;
        }
        if (err.code === 'NOT_FOUND') {
          setError('not-found');
          showError(SHARE_ERROR_MESSAGES.LINK_NOT_FOUND);
          scheduleRedirect('/');
          return null;
        }
        throw err;
      });

      if (!snapshot) return;

      const customerGroupMap = await loadCustomerGroupMap();

      const productIds = snapshot.lineItems.map((item) => item.productId);
      const fetchedProducts = await Promise.all(productIds.map(loadProductById));
      const validProducts = fetchedProducts.filter((p): p is CtProduct => p !== null);

      const missingCount = productIds.length - validProducts.length;
      if (missingCount > 0) {
        if (validProducts.length === 0) {
          setError('no-products');
          showError(SHARE_ERROR_MESSAGES.PRODUCTS_UNAVAILABLE);
          scheduleRedirect('/');
          return;
        }
        showError(SHARE_WARNING_MESSAGES.SOME_PRODUCTS_MISSING(missingCount));
      }

      const productMap: Record<string, CtProduct> = {};
      validProducts.forEach((product) => {
        productMap[product.id] = product;
      });

      setSharedBasket({ ...snapshot, productMap, customerGroupMap });
    } catch (err) {
      console.error('[useViewSharedBasket] Failed to load shared basket:', err);
      setError('general');
      showError(SHARE_ERROR_MESSAGES.ADD_TO_CART_FAILED);
      scheduleRedirect('/');
    } finally {
      setLoading(false);
    }
  }, [shareToken, showError, scheduleRedirect]);

  useEffect(() => {
    fetchSharedBasket();
    return () => {
      if (navTimerRef.current !== null) clearTimeout(navTimerRef.current);
    };
  }, [fetchSharedBasket]);

  return { loading, error, sharedBasket };
}
